// Min profil (admin mockup "profile"): what belongs to the person, not the site — theme
// and language, saved on the user record and applied wherever they log in. The login
// section is self-service (owner 2026-10-28): password against the current one, the short
// code for the handheld, and pairing a new one-time-code app.

import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";
import { applyPrefs, Auth, effectiveTheme, getSession, saveSession, type UserSummary } from "../api/client.js";
import { getLang, setLang, t, type Lang } from "../i18n.js";

type Theme = "" | "dark" | "light" | "auto";

export function Profile() {
  const session = getSession();
  const [theme, setTheme] = useState<Theme>(session?.theme ?? "");
  const [lang, setLangState] = useState<Lang>(session?.language ?? getLang());
  const [msg, setMsg] = useState<string | null>(null);
  const [account, setAccount] = useState<{ loginMethod: "password" | "entra"; methods: UserSummary["methods"] | null } | null>(null);
  const [pw, setPw] = useState({ current: "", next: "", again: "" });
  const [shortCode, setShortCode] = useState("");
  const [loginMsg, setLoginMsg] = useState<string | null>(null);
  const [mfa, setMfa] = useState<{ secret: string; otpauth: string } | null>(null);
  const mfaCanvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    Auth.account().then(setAccount).catch(() => setAccount(null));
  }, []);
  useEffect(() => {
    if (mfa && mfaCanvas.current) void QRCode.toCanvas(mfaCanvas.current, mfa.otpauth, { width: 180, margin: 1 });
  }, [mfa]);
  const changePassword = async () => {
    setLoginMsg(null);
    try {
      const r = await Auth.updateMe({ currentPassword: pw.current, newPassword: pw.next });
      setAccount((a) => (a ? { ...a, methods: r.account?.methods ?? a.methods } : a));
      setPw({ current: "", next: "", again: "" });
      setLoginMsg(t("adm.me.pwChanged"));
    } catch (e) {
      setLoginMsg(/wrong_password/.test(String(e)) ? t("adm.me.pwWrong") : e instanceof Error ? e.message : String(e));
    }
  };
  const saveShortCode = async (clear = false) => {
    setLoginMsg(null);
    try {
      const r = await Auth.updateMe({ shortCode: clear ? null : shortCode });
      setAccount((a) => (a ? { ...a, methods: r.account?.methods ?? a.methods } : a));
      setShortCode("");
      setLoginMsg(clear ? t("adm.me.codeCleared") : t("adm.me.codeSaved"));
    } catch (e) {
      setLoginMsg(e instanceof Error ? e.message : String(e));
    }
  };
  const pairMfa = async () => {
    if (!session) return;
    if (account?.methods?.mfa && !window.confirm(t("adm.me.mfaConfirm"))) return;
    try {
      setMfa(await Auth.myMfa(session.tenantId, session.userId));
      setAccount((a) => (a && a.methods ? { ...a, methods: { ...a.methods, mfa: true } } : a));
    } catch (e) {
      setLoginMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const save = async () => {
    if (!session) return;
    try {
      const r = await Auth.updateMe({ theme: theme || null, language: lang });
      saveSession({ ...session, theme: r.theme, language: r.language });
      setLang(r.language);
      applyPrefs(undefined, "admin");
      setMsg(t("adm.me.saved"));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const now = effectiveTheme(undefined, "admin");
  const nowText = theme ? t("adm.me.now.user", { theme: themeLabel(theme) }) : t("adm.me.now.device", { here: themeLabel(now), phone: themeLabel(effectiveTheme(undefined, "task")) });

  return (
    <>
      <div className="adm-top">
        <div><h1>{session?.name ?? ""}</h1><div className="crumb">{t("adm.me.title")} › {session?.email ?? ""}</div></div>
        <div className="right"><button type="button" className="abtn" onClick={() => { if (confirm(t("adm.me.logoutAllConfirm"))) void Auth.logoutAll().then(() => { saveSession(null); window.location.href = "/"; }); }}>{t("adm.me.logoutAll")}</button><button type="button" className="abtn primary" onClick={() => void save()}>{t("settings.save")}</button></div>
      </div>
      <div className="adm-content">
        {msg && <p className="ahint" style={{ color: "var(--ok)" }}>{msg}</p>}
        <div className="adm-cols">
          <div className="card adm-w460">
            <div className="hd">{t("adm.me.appearance")} <span className="sub">{t("adm.me.appearance.sub")}</span></div>
            <div className="bd aform" style={{ gridTemplateColumns: "110px 1fr" }}>
              <span>{t("settings.theme")}</span>
              <div className="achips">
                {(["", "dark", "light", "auto"] as const).map((v) => <button key={v || "device"} type="button" className={`achip${theme === v ? " on" : ""}`} onClick={() => setTheme(v)}>{themeLabel(v)}</button>)}
              </div>
              <span></span><p className="ahint">{nowText}</p>
              <span>{t("settings.lang")}</span>
              <div className="achips">
                <button type="button" className={`achip${lang === "sv" ? " on" : ""}`} onClick={() => setLangState("sv")}>Svenska</button>
                <button type="button" className={`achip${lang === "en" ? " on" : ""}`} onClick={() => setLangState("en")}>English</button>
              </div>
              <span></span><p className="ahint">{t("adm.me.langNote")}</p>
            </div>
          </div>
          <div className="card adm-grow">
            <div className="hd">{t("adm.me.login")} <span className="sub">{t("adm.me.login.sub")}</span></div>
            <div className="bd aform" style={{ gridTemplateColumns: "170px 1fr" }}>
              <span>{t("adm.me.email")}</span><div className="ain mono" style={{ display: "flex", alignItems: "center" }}>{session?.email ?? ""}</div>
              <span>{t("adm.du.roles")}</span><div>{session?.permissions.includes("users.manage") ? t("adm.role.administrator") : session?.permissions.includes("config.edit") ? t("adm.role.lead") : t("adm.role.operator")}</div>
              <span>{t("adm.me.onDesktop")}</span><div>{account?.loginMethod === "entra" ? t("adm.du.lm.entra") : account?.methods?.mfa ? t("adm.du.m.passwordMfa") : t("adm.du.lm.password")}</div>
              <span>{t("adm.me.onDevice")}</span><div>{account?.methods?.shortCode ? t("adm.me.codeSet") : t("adm.me.codeUnset")}</div>
              {loginMsg && <><span></span><p className="ahint" style={{ color: /fel|wrong|misslyck/i.test(loginMsg) ? "var(--bad)" : "var(--ok)" }}>{loginMsg}</p></>}
              {account?.loginMethod === "password" && (
                <>
                  <span>{t("adm.me.pwCurrent")}</span><input className="ain" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" />
                  <span>{t("adm.me.pwNew")}</span><input className="ain" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" placeholder={t("adm.me.pwHint")} />
                  <span>{t("adm.me.pwAgain")}</span><input className="ain" type="password" value={pw.again} onChange={(e) => setPw({ ...pw, again: e.target.value })} autoComplete="new-password" />
                  <span></span><div><button type="button" className="abtn" disabled={pw.next.length < 10 || pw.next !== pw.again || (!!account.methods?.password && !pw.current)} onClick={() => void changePassword()}>{t("adm.me.pwChange")}</button></div>
                  <span>{t("adm.me.mfa")}</span><div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}><span className="mut">{account.methods?.mfa ? t("adm.me.mfaOn") : t("adm.me.mfaOff")}</span><button type="button" className="abtn sm" onClick={() => void pairMfa()}>{account.methods?.mfa ? t("adm.me.mfaRepair") : t("adm.me.mfaPair")}</button></div>
                  {mfa && <><span></span><div className="qrbox"><canvas ref={mfaCanvas} /><p className="ahint">{t("adm.du.mfaHow")}<br /><code>{mfa.secret}</code></p></div></>}
                </>
              )}
              <span>{t("adm.du.shortCode")}</span><div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><input className="ain mono" style={{ width: 140 }} inputMode="numeric" value={shortCode} onChange={(e) => setShortCode(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder={account?.methods?.shortCode ? t("adm.du.keep") : "4–8 siffror"} /><button type="button" className="abtn sm" disabled={shortCode.length < 4} onClick={() => void saveShortCode()}>{t("settings.save")}</button>{account?.methods?.shortCode && <button type="button" className="abtn sm" onClick={() => void saveShortCode(true)}>{t("adm.me.codeClear")}</button>}</div>
              <span></span><p className="ahint">{t("adm.me.login.note")}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function themeLabel(v: Theme | "dark" | "light" | "auto"): string {
  return v === "dark" ? t("settings.theme.dark") : v === "light" ? t("settings.theme.light") : v === "auto" ? t("settings.theme.auto") : t("settings.theme.device");
}
